import { SystemMode, Location, LocationType, Unit, UnitSystem, Device, getRoleFromPermissions, Extendable } from  'model';
import { LocationRolePayload, toLocationRole, UserRolePayload, toUserRole } from './Role';
import { keyBy, isEmpty, fromPairs } from 'lodash';
import { DeviceResponse, toDevice, SystemModeValues } from './Device';
import { LocationFilters, LocationFacet, LocationFacetType } from 'model/Location';
import { LocationFacetsState } from 'model/state';
import { AlertLocationResponse, AlertResponse, toAlert } from "./Alert";


interface SystemModeResponse {
  target: 'home' | 'away' | 'sleep'
}

interface NotificationStats {
  infoCount: number;
  warningCount: number;
  criticalCount: number;
}

interface Notifications {
  pending: NotificationStats
}

interface ClassType {
  key: string;
  level?: number;
}

export interface LocationResponse {
  id: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  country: string;
  postalCode?: string;
  timezone: string;
  nickname: string;
  class: ClassType;
  notifications: Notifications;
  children: Extendable<LocationResponse>[];
  parent?: Extendable<LocationResponse> | null;
  systemMode: SystemModeResponse;
  isProfileComplete?: boolean;
  userRoles: UserRolePayload[];
}

export interface UnitResponse extends LocationResponse {
  devices: Extendable<DeviceResponse>[];
}

export interface LocationPayload {
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  timezone?: string;
  nickname?: string;
  parent?: { id: string } | null;
  class?: ClassType;
  account?: { id: string };
  isProfileComplete?: boolean;
}

export interface LocationSystemModePayload {
  target: SystemModeValues;
  revertMode?: SystemModeValues;
  revertMinutes?: number;
  shouldCascade: boolean;
}

export interface GetLocationsResponse {
  items: LocationResponse[];
  total: number;
  page: number;
}

export interface GetLocationsParameters {
  userId: string;
  class?: string;
  q?: string;
  withChildren?: 'true' | 'false';
  rootOnly?: 'true' | 'false';
  page?: number;
  size?: number;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  parentId?: string;
  sort?: string;
  cachePolicy?: string;
  hasOfflineDevices?: string;
}

type LocationFacetName = 'postalCode' | 'city' | 'country' | 'state' | 'class';

export interface GetLocationsFacetsParameters {
  userId: string;
  name: LocationFacetName[];
  contains?: string;
  page?: number;
  size?: number;
}

export type LocationFaucetResponse = {
  name: string;
  items: Array<string | null>;
  page: number;
  total: number;
}

export interface GetLocationsFacetResponse {
  items: LocationFaucetResponse[];
  page: number;
}

const toLocationType = (type: ClassType): LocationType => {
  switch(type.key) {
    case 'unit':
      return LocationType.UNIT;
    case 'property':
      return LocationType.PROPERTY;
    case 'building':
      return LocationType.BUILDING;
    case 'community':
    case 'region':
      return LocationType.REGION;
    default:
      return LocationType.UNIT;
  }
}

export const fromLocationType = (type: LocationType): ClassType => {
  switch(type) {
    case  LocationType.UNIT:
      return { key: 'unit' };
    case LocationType.PROPERTY:
      return { key: 'property' };
    case LocationType.BUILDING:
      return { key: 'building' };
    case LocationType.REGION:
      return { key: 'region' };
    default:
      return { key: 'unit' };
  }
}

const toSystemMode = (systemMode: SystemModeResponse): SystemMode => {
  switch(systemMode.target) {
    case 'home':
      return SystemMode.HOME;
    case 'sleep':
      return SystemMode.SLEEP;
    case 'away':
      return SystemMode.AWAY;
    default:
      return SystemMode.HOME;
  }
}

export const toLocation = (data: LocationResponse): Location => ({
  id: data.id,
  address: data.address,
  address2: data.address2,
  city: data.city,
  state: data.state?.toUpperCase(),
  country: data.country,
  postalCode: data.postalCode,
  timezone: data.timezone,
  nickname: data.nickname,
  isProfileComplete: data.isProfileComplete,
  type: toLocationType(data.class),
  notifications: {
    pending: {
      infoCount: data.notifications.pending.infoCount,
      warningCount: data.notifications.pending.warningCount,
      criticalCount: data.notifications.pending.criticalCount,
    }
  },
  children: data.children.map(loc => {
    return ((loc as LocationResponse).address) ? toLocation(loc as LocationResponse) : { id: loc.id }
  }),
  parent: data.parent,
  systemMode: {
    target: toSystemMode(data.systemMode),
  },
});

export const toUnit = (unit: UnitResponse, userUnitSystem: UnitSystem, authUserId: string): Unit => {
  const userRole = toUserRole(unit.userRoles, authUserId);

  let hasNonAuthParent = true;
  if (unit.parent) {
    hasNonAuthParent = (unit.parent as LocationResponse).userRoles === undefined;
  }

  return {
    ...toLocation(unit),
    roles: userRole.roles,
    hasInheritedRolesOnly: userRole.isInherited,
    devices: unit.devices.reduce((acc: Device[], dev) => {
      return (dev as DeviceResponse).deviceType ? [ ...acc, toDevice(dev as DeviceResponse, userUnitSystem) ] : acc
    }, []),
    parent: hasNonAuthParent ? undefined : unit.parent,
  }
}

const someOrDefault = (value: any, defaultValue: any = undefined) => value ? value : defaultValue;

export const fromLocation = (data: Partial<Location>): LocationPayload => ({
  address: someOrDefault(data.address),
  address2: data.address2,
  city: someOrDefault(data.city),
  state: someOrDefault(data.state),
  country: data.country,
  postalCode: someOrDefault(data.postalCode),
  timezone: data.timezone,
  nickname: data.nickname,
  class: (data.id !== undefined || data.type === undefined) ? undefined : fromLocationType(data.type),
  account: data.account,
  isProfileComplete: true,
  parent: data.parent?.id ? {
    id: data.parent?.id
  }: undefined
})

export const toLocationsWithRolesUsingLocationRoles = (
  locations:  Array<Extendable<LocationResponse>>,
  locationRoles:  LocationRolePayload[]
): Location[] => {
  const roleMap = keyBy(toLocationRole(locationRoles), loc => loc.locationId);

  return locations.map(loc => ({
    ...toLocation(loc as LocationResponse),
    roles: roleMap[loc.id]? roleMap[loc.id].roles : [],
    hasInheritedRolesOnly: roleMap[loc.id] ? roleMap[loc.id].isInherited : false,
  }))
}

export const toLocationsWithRoles = (
  locations:  Array<LocationResponse>,
  userId:  string
): Location[] => locations.map(loc => {
  const userRole = toUserRole(loc.userRoles, userId);
  return {
    ...toLocation(loc as LocationResponse),
    roles: userRole.roles,
    hasInheritedRolesOnly: userRole.isInherited,
  }
})


export const filterLocationsWithNoParent = (locations: Location[], locationRoles: LocationRolePayload[]): Location[] => {
  const roleArray = toLocationRole(locationRoles)?.map(locRole => ({
    locationId: locRole.locationId,
    role: getRoleFromPermissions(locRole.roles)
  }));
  const roleMap = keyBy(roleArray, loc => loc.locationId);

  return locations.filter(loc => {
    return !loc.parent || !roleMap[loc.parent.id || ''] || roleMap[loc.parent.id || ''].role === undefined
  })
}

export const removeNonAuthorizedParent = (locationResponse: LocationResponse, userId: string): Location => {
  const location = toLocation(locationResponse);
  if (!location.parent) {
    return location;
  }
  const hasNonAuthParent = (locationResponse.parent as LocationResponse).userRoles === undefined;
  return {
    ...location,
    parent: hasNonAuthParent ? undefined : location.parent
  }
}

export const buildLocationQuery = (userId: string, page: number, filters: LocationFilters, search: string | undefined, sort: string = '', parentId?: string,
  isRootLevel: boolean = true, pageSize: number = 20, cached: boolean = true,
): GetLocationsParameters => ({
  userId,
  q: search,
  rootOnly: isRootLevel ? 'true' : 'false',
  page,
  size: pageSize,
  class: (filters.type !== null && filters.type !== undefined) ? fromLocationType(filters.type as LocationType).key : undefined,
  city: !isEmpty(filters.cities) ? filters.cities?.join(',') : undefined,
  country: !isEmpty(filters.countries) ? filters.countries?.join(',') : undefined,
  postalCode: !isEmpty(filters.zipCodes) ? filters.zipCodes?.join(',') : undefined,
  state: !isEmpty(filters.states) ? filters.states?.join(',') : undefined,
  sort: !isEmpty(sort) ? sort : undefined,
  parentId,
  cachePolicy: cached ? undefined : 'off',
  hasOfflineDevices: filters.offlineDevices ? 'true' : undefined,
});

export const buildSortQuery = (sortProperties: string[] = []): string => sortProperties.join(',')

export const fromLocationFacetType = (type: LocationFacetType): LocationFacetName => {
  switch(type) {
    case LocationFacetType.CITY:
      return 'city';
    case LocationFacetType.CLASS:
      return 'class';
    case LocationFacetType.COUNTRY:
      return 'country';
    case LocationFacetType.POSTAL_CODE:
      return 'postalCode';
    case LocationFacetType.STATE:
      return 'state';
  }
}

export const toFacetParameters = (facet: LocationFacet): GetLocationsFacetsParameters => ({
  userId: facet.userId,
  name: facet.type !== undefined ? [fromLocationFacetType(facet.type)] : ['postalCode', 'city', 'country', 'state', 'class'],
  contains: facet.search,
  page: facet.page,
  size: 15
});

export const toLocationFacetData = (response: GetLocationsFacetResponse): LocationFacetsState => {
  return fromPairs(response.items.map(it => [ it.name, {
      rows: it.items.filter(f => f) as Array<string>,
      page: it.page,
      total: it.total,
      isLoading: false,
  }]));
}

export const toLocationFromPendingAlert = (data: AlertResponse): Location => {
  if (!data.location?.address) {
    throw Error('Location data is missing in the alert object');
  }
  const location = data.location as AlertLocationResponse;
  return {
    id: data.locationId,
    address: location.address,
    address2: location.address2,
    city: location.city,
    state: location.state,
    country: location.country || '',
    postalCode: location.postalCode,
    timezone: location.timezone,
    nickname: location.nickname,
    type: LocationType.UNIT,
    notifications: {
      pending: {
        warningCount: data.alarm.severity === 'warning' ? 1 : 0,
        criticalCount: data.alarm.severity === 'critical' ? 1 : 0,
        infoCount: data.alarm.severity === 'info' ? 1 : 0,
      }
    },
    children: [],
    alerts: [ toAlert(data) ],
    geoLocation: {
      coordinates: location.geoLocation?.coordinates ? {
        latitude: location.geoLocation.coordinates.latitude,
        longitude: location.geoLocation.coordinates.longitude,
      } : undefined
    }
  }
};
